/**
 * Quest Complete Component
 * Shows ending, rewards, and unlocks
 */

import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonBadge,
  IonIcon,
  IonText,
  IonList,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import {
  trophyOutline,
  starOutline,
  giftOutline,
  lockOpenOutline,
  homeOutline,
} from 'ionicons/icons';
import { QuestEnding } from '../../../types/pipsGuildQuest';
import './QuestComplete.css';

interface QuestCompleteParams {
  questId: string;
}

interface QuestCompleteProps {
  ending?: QuestEnding;
  goldEarned?: number;
  xpEarned?: number;
  itemsUnlocked?: Array<{ name: string; description: string }>;
  classesUnlocked?: Array<{ name: string; description: string }>;
  achievementsUnlocked?: Array<{ name: string; description: string }>;
  onReturnToHub?: () => void;
}

const QuestComplete: React.FC<QuestCompleteProps> = ({
  ending,
  goldEarned = 0,
  xpEarned = 0,
  itemsUnlocked = [],
  classesUnlocked = [],
  achievementsUnlocked = [],
  onReturnToHub,
}) => {
  const history = useHistory();

  const handleReturnToHub = () => {
    if (onReturnToHub) {
      onReturnToHub();
    } else {
      history.push('/games/pips-guild-quest');
    }
  };

  const getEndingIcon = (endingType?: string) => {
    switch (endingType) {
      case 'true':
        return '👑';
      case 'good':
        return '⭐';
      case 'neutral':
        return '🏅';
      case 'bad':
        return '💀';
      case 'secret':
        return '🔮';
      default:
        return '🎉';
    }
  };

  const getEndingColor = (endingType?: string) => {
    switch (endingType) {
      case 'true':
        return 'warning';
      case 'good':
        return 'success';
      case 'neutral':
        return 'medium';
      case 'bad':
        return 'danger';
      case 'secret':
        return 'tertiary';
      default:
        return 'primary';
    }
  };

  return (
    <div className="quest-complete">
      {/* Celebration Header */}
      <div className="celebration-header">
        <div className="celebration-icon">{getEndingIcon(ending?.endingType)}</div>
        <h1>Quest Complete!</h1>
        {ending && (
          <>
            <h2>{ending.title}</h2>
            <IonBadge color={getEndingColor(ending.endingType)} className="ending-badge">
              {ending.endingType?.toUpperCase()} ENDING
            </IonBadge>
          </>
        )}
      </div>

      {/* Ending Story */}
      {ending && (
        <IonCard className="ending-card">
          <IonCardContent>
            <div className="ending-text">{ending.endingText}</div>
          </IonCardContent>
        </IonCard>
      )}

      {/* Rewards Summary */}
      <IonCard className="rewards-card">
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={giftOutline} /> Rewards
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <div className="rewards-grid">
            <div className="reward-item">
              <div className="reward-icon">💰</div>
              <div className="reward-value">{goldEarned}</div>
              <div className="reward-label">Gold</div>
            </div>

            <div className="reward-item">
              <div className="reward-icon">✨</div>
              <div className="reward-value">{xpEarned}</div>
              <div className="reward-label">Experience</div>
            </div>

            {itemsUnlocked.length > 0 && (
              <div className="reward-item">
                <div className="reward-icon">🎁</div>
                <div className="reward-value">{itemsUnlocked.length}</div>
                <div className="reward-label">Items</div>
              </div>
            )}

            {classesUnlocked.length > 0 && (
              <div className="reward-item">
                <div className="reward-icon">⚔️</div>
                <div className="reward-value">{classesUnlocked.length}</div>
                <div className="reward-label">Classes</div>
              </div>
            )}
          </div>
        </IonCardContent>
      </IonCard>

      {/* Unlocked Content */}
      {(itemsUnlocked.length > 0 ||
        classesUnlocked.length > 0 ||
        achievementsUnlocked.length > 0) && (
        <IonCard className="unlocks-card">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={lockOpenOutline} /> Unlocked
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {classesUnlocked.length > 0 && (
              <div className="unlock-section">
                <h3>
                  <IonIcon icon={starOutline} /> New Classes
                </h3>
                <IonList>
                  {classesUnlocked.map((classItem, index) => (
                    <IonItem key={index} lines="none" className="unlock-item">
                      <IonLabel>
                        <h4>{classItem.name}</h4>
                        <p>{classItem.description}</p>
                      </IonLabel>
                      <IonBadge color="success" slot="end">
                        NEW
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            {itemsUnlocked.length > 0 && (
              <div className="unlock-section">
                <h3>
                  <IonIcon icon={giftOutline} /> New Items
                </h3>
                <IonList>
                  {itemsUnlocked.map((item, index) => (
                    <IonItem key={index} lines="none" className="unlock-item">
                      <IonLabel>
                        <h4>{item.name}</h4>
                        <p>{item.description}</p>
                      </IonLabel>
                      <IonBadge color="primary" slot="end">
                        NEW
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            {achievementsUnlocked.length > 0 && (
              <div className="unlock-section">
                <h3>
                  <IonIcon icon={trophyOutline} /> Achievements
                </h3>
                <IonList>
                  {achievementsUnlocked.map((achievement, index) => (
                    <IonItem key={index} lines="none" className="unlock-item">
                      <IonLabel>
                        <h4>{achievement.name}</h4>
                        <p>{achievement.description}</p>
                      </IonLabel>
                      <IonBadge color="warning" slot="end">
                        NEW
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}
          </IonCardContent>
        </IonCard>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <IonButton
          expand="block"
          size="large"
          color="primary"
          onClick={handleReturnToHub}
        >
          <IonIcon icon={homeOutline} slot="start" />
          Return to Quest Hub
        </IonButton>

        {ending?.endingType === 'true' && (
          <IonText color="success" className="ion-text-center">
            <p>
              <strong>
                Congratulations on achieving the true ending! You've unlocked special rewards!
              </strong>
            </p>
          </IonText>
        )}
      </div>
    </div>
  );
};

export default QuestComplete;
